/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {Injectable, OnDestroy} from "@angular/core";
import {ActivatedRoute, NavigationExtras, Params, QueryParamsHandling, Router, Routes} from "@angular/router";
import {RouteNode} from '@core/models';
import {INavMenu, navItems} from '@layouts/components';
import {ROLES} from '@shared/models/user';
import {AuthService} from "@core/authentication/auth.service";
import {PermissionService} from "@core/authentication/permission.service";
import {BehaviorSubject, Subject} from "rxjs";
import {debounceTime, takeUntil} from "rxjs/operators";

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  public navItemsAllows: INavMenu[] = []; // list menu cho phép (sync)
  public navItemsAllows$ = new BehaviorSubject<INavMenu[]>([]); // list menu cho phép (async)

  private destroy$ = new Subject<void>();
  private regenerateDebouncer: any;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private permissionService: PermissionService
  ) {
    this.authService.activeBusiness$.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.scheduleRegenerate();
    });

    this.authService.businesses$.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.scheduleRegenerate();
    });

    this.permissionService.permissions$.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.scheduleRegenerate();
    });

    // Lắng nghe sự kiện chọn page xong để nạp lại menu ngay lập tức
    this.authService.isSelectPage.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.scheduleRegenerate();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.regenerateDebouncer) {
      clearTimeout(this.regenerateDebouncer);
    }
  }

  /**
   * Hẹn giờ 100ms sinh lại menu phòng call dồn dập
   */
  private scheduleRegenerate() {
    if (this.regenerateDebouncer) {
      clearTimeout(this.regenerateDebouncer);
    }
    this.regenerateDebouncer = setTimeout(() => {
      this.regenerateMenu();
    }, 100);
  }

  /**
   * Tạo lại mảng menu cho phép theo role hoặc customRole
   */
  public regenerateMenu() {
    // Bước 1: Kiểm tra người dùng đã chọn page và có doanh nghiệp liên kết hay chưa (chuẩn logic v1: dùng getBusinesses thay vì getBusinessesManagement để hỗ trợ tài khoản nhân viên)
    if (this.authService.hasJoinPages() && this.authService.getBusinesses()?.length) {
      // Bước 2: Kiểm tra loại phân quyền (Custom Role hay System Role) và danh sách quyền chi tiết
      const isCustomRole = this.authService.isCustomRole();
      const currentPermissions = this.permissionService.getCurrentPermissions();

      // Bước 3: Lọc danh sách menu được phép hiển thị và phát ra Subject cho các component Header/Sidebar lắng nghe
      this.navItemsAllows = this.filterNavItems(navItems, isCustomRole, currentPermissions);
      this.navItemsAllows$.next(this.navItemsAllows);
    }
  }

  /**
   * Kiểm tra quyền truy cập menu hệ thống (admin / sys):
   * Tương đương bản cũ v1:
   * - SuperAdmin, Admin và Blog_Editor có quyền truy cập
   * - Các role doanh nghiệp khác (Staff, Manager, Page Admin, Custom Role) đều bị chặn
   */
  private canAccessSystemMenu(isCustomRole: boolean): boolean {
    if (this.authService.isAdmin()) {
      return true;
    }
    // Nếu không phải custom role của doanh nghiệp và có quyền Blog_Editor (hoặc Admin/SuperAdmin)
    if (!isCustomRole && this.authService.isAllowed(ROLES.Blog_Editor)) {
      return true;
    }
    return false;
  }

  /**
   * Hàm đệ quy lọc menu theo quyền
   */
  private filterNavItems(items: INavMenu[], isCustomRole: boolean, permissions: string[], parentPath: string = ''): INavMenu[] {
    return items.map(item => {
      if (!item?.display) return null;

      // Menu hệ thống (admin): Ở v2 đường dẫn là /admin/xxx (ở v1 là /sys/xxx)
      // Theo logic chuẩn v1: SuperAdmin, Admin và Blog_Editor mới được hiển thị menu này
      if ((item.path === 'admin' || item.path === 'sys') && !this.canAccessSystemMenu(isCustomRole)) {
        return null;
      }

      const menuItem: INavMenu = {...item};

      // Ưu tiên originTitle nếu có (để hiển thị trực tiếp không qua dịch)
      if (item.originTitle && !item.title) {
        menuItem.title = item.originTitle;
      }

      let hasVisibleChildren = false;

      // Xử lý đệ quy cho các menu con
      if (item.children && item.children.length > 0) {
        const currentPath = (parentPath ? parentPath + '/' : '') + (item.path || '');
        // Đối với các mục con thuộc menu hệ thống (admin/sys), kiểm tra theo quyền hệ thống (System Role) thay vì Custom Role của DN
        const isChildCustomRole = (
          item.path === 'admin' || item.path === 'sys' ||
          parentPath === 'admin' || parentPath === 'sys' ||
          parentPath.startsWith('admin/') || parentPath.startsWith('sys/')
        ) ? false : isCustomRole;
        menuItem.children = this.filterNavItems(item.children, isChildCustomRole, permissions, currentPath);
        hasVisibleChildren = menuItem.children.length > 0;
      }

      const isAllowed = this.hasAccessToRoute(item, isCustomRole, permissions);

      // Điều kiện hiển thị:
      // 1. Bản thân nó có quyền (isAllowed)
      // 2. HOẶC có ít nhất một con hiển thị được (hasVisibleChildren)
      if (isAllowed || hasVisibleChildren) {
        // Cập nhật lại đường dẫn tuyệt đối cho menu để router hoạt động đúng
        if (parentPath && item.path && !item.path.startsWith('/')) {
          menuItem.path = parentPath + '/' + item.path;
        }
        return menuItem;
      }

      return null;
    }).filter(item => item !== null) as INavMenu[];
  }

  private hasAccessToRoute(route: { role?: any; permission?: string; path?: string }, isCustomRole: boolean, permissions: string[]): boolean {
    // Menu hệ thống (admin): Ở v2 prefix là /admin (v1 là /sys), route yêu cầu permission 'system':
    // Theo logic v1: SuperAdmin, Admin và Blog_Editor mới có quyền truy cập
    if (route.permission === 'system' || route.path === 'admin' || route.path === 'sys') {
      return this.canAccessSystemMenu(isCustomRole);
    }

    // Nếu là custom role, chỉ kiểm tra permissions
    if (isCustomRole) {
      // Nếu route có permission, kiểm tra permission
      if (route.permission) {
        if (!permissions || !Array.isArray(permissions)) return false;
        return permissions.some(p => String(p).trim() === route.permission);
      }

      // Nếu route không có permission, cho phép truy cập
      return true;
    }

    // Nếu không phải custom role, dùng logic role cũ (System Role)
    return this.authService.isAllowed(route.role);
  }

  subQueryParams() {
    return this._activatedRoute.queryParams;
  }

  getParam(param: string) {
    return this._activatedRoute.snapshot.queryParams[param]
  }

  getParams() {
    return this._activatedRoute.snapshot.queryParams;
  }

  private getParamsTrack(params: any) {
    for (const param in this._activatedRoute.snapshot.queryParams) {
      if (this._activatedRoute.snapshot.queryParams[param])
        if (params[param]) {
          params[param] = this._activatedRoute.snapshot.queryParams[param];
        }
    }
  }

  /**
   * Thay thế lại toàn bộ params
   * */
  setParams(params = {}, paramsHandling: QueryParamsHandling = '', fromPage?: any) {
    // console.log('setParams', {
    // params, paramsHandling, fromPage
    // })
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: params,
      queryParamsHandling: paramsHandling,
      replaceUrl: true
    }).catch(() => {
    });
  }

  /**
   * Thêm hoặc sửa nhiều param
   * */
  addParams(params: Params) {
    const currentParams = this.getParams();
    this.setParams({...currentParams, ...params})
  }

  /**
   * Xóa params khỏi url
   * */
  removeParams(params: string[]) {
    if (!params || !params.length) {
      return;
    }

    const urlTree = this._router.parseUrl(this._router.url);
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const newParams = {...queryParams};
    for (const par of params) {
      if (newParams[par]) {
        delete urlTree.queryParams[par];
      }
    }

    this._router.navigateByUrl(urlTree.toString()).catch(() => {
      // Xử lý lỗi nếu có
    });
  }

  redirect(commands: any[], extras?: NavigationExtras) {
    this._router.navigate(commands, extras).then();
  }

  get currentUri(): string {
    return this._router.url;
  }

  /**
   * Hàm đệ quy xây dựng cây route theo dạng cha - con.
   * @param routes Mảng các route cần duyệt
   * @param parentPath Đường dẫn của route cha (mặc định là chuỗi rỗng)
   * @returns Mảng các đối tượng RouteNode thể hiện cấu trúc cha - con.
   */
  buildRouteTree(routes: Routes, parentPath: string = ''): RouteNode[] {
    const isCustomRole = this.authService.isCustomRole();
    const currentPermissions = this.permissionService.getCurrentPermissions();

    return routes
      .filter(route => {
        if (route.data) {
          if (route.data['hideMenu']) {
            return false;
          }
          if (!this.hasAccessToRoute(route.data, isCustomRole, currentPermissions)) {
            return false;
          }
        }
        return true;
      })
      .map(route => {
        // Ghép đường dẫn của route hiện tại với parentPath.
        const currentPath = parentPath + (route.path ? '/' + route.path : '');
        const node: RouteNode = {
          path: route.path || '',
          fullPath: currentPath,
          component: route.component,
          data: route.data,
        };

        if (node.path === 'fbad') {
          node.data = {...node.data, originTitle: 'Facebook Ads'};
        } else if (node.path === 'livestreams') {
          node.data = {...node.data, originTitle: 'Live Streams'};
        }

        // Sync originTitle to title if title is missing
        if (node.data && node.data.originTitle && !node.data.title) {
          node.data.title = node.data.originTitle;
        }

        // Nếu route có children, gọi đệ quy để xử lý.
        if (route.children) {
          node.children = this.buildRouteTree(route.children, currentPath);
        }
        return node;
      });
  }
}
